/**
 * ColumnCheckmarkRow — `column.checkmark` renderer. Paints ☐ / ☑ in the
 * `column` slot and edits its parent dataSource's boolean cells.
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import type { NaiveDate } from "@tento-chrono";

import type { Actor } from "../../tx/invalidation.ts";
import type { ModelRowOptions } from "../row-options.ts";
import { RendererRow } from "./renderer-row.ts";
import { SourceCells } from "./source-cells.ts";
import { SourceRow } from "./source-row.ts";
import { sourceWriterOf } from "./source-row.ts";

/**
 * **The writers of a `column.checkmark` renderer** — checked write and toggle
 * of its source row's boolean cells (`ColumnCheckmarkRow.Writer`), through
 * the source's checked writer (`sourceWriterOf`). Not on the row: a
 * row a read hands out (`handle.model`, `row.variant`, `RowKind.of`) —
 * and the live class `liveRowOf` reaches — carries no writer. The package's
 * own ops write through it, and this repository's tests through
 * `@tento-lona/sheets/internal-testing`; an app and the SDK's own front-ends
 * (sheets-ui, the CLI) write the cell through the SDK row's checked verbs
 * (`row.writableSingle()?.write(CellTarget.date(day), value, actor)`).
 */
export function columnCheckmarkWriterOf(row: ColumnCheckmarkRow): ColumnCheckmarkRow.Writer {
  const write = async (date: NaiveDate.Partial, checked: boolean, actor: Actor): Promise<void> => {
    await sourceWriterOf(row.source).write<boolean>(date, checked, null, actor);
  };
  return Object.freeze({
    row,
    write,
    toggle: (date: NaiveDate.Partial, actor: Actor) => write(date, !row.peekChecked(date), actor),
  });
}

/** The `column.checkmark` renderer row: paints its source row's checkmark cells. */
export class ColumnCheckmarkRow extends RendererRow {
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnCheckmarkRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}).`,
      );
    }
    return p;
  }

  /**
   * False when the source rejects cell writes (formula rows,
   * provider-owned `metadata.readOnly` rows) — the UI skips the
   * toggle instead of letting a write throw.
   */
  get editable(): boolean {
    // Detached renderer (stale onData after in-place structural mutation)
    // reports non-editable instead of letting `source` throw. See ColumnTextRow.
    const p = this.parent;
    return p instanceof SourceRow ? p.cellsWritable : false;
  }

  /** `false` when the cell is unset (parity with the current CheckmarkRow). */
  peekChecked(date: NaiveDate.Partial): boolean {
    const cell = SourceCells.dates(this.source.peekCells(date.toRange()))[0];
    return cell == null ? false : (cell.value as Option<boolean>) ?? false;
  }



  /**
   * CSS color for the ☑ glyph. Defaults to `var(--green)`. Copy-on-write
   * dual-read: the renderer's OWN bag wins once the default renderer is
   * materialized + customized; otherwise falls back to the SOURCE's bag,
   * where the color lives until first customization (and for never-customized
   * rows).
   */
  get checkColor(): string {
    return (
      Json.string(this.attributes(), "checkColor") ??
      Json.string(this.source.attributes(), "checkColor") ??
      "var(--green)"
    );
  }

  /**
   * Glyph-color option. Copy-on-write: customizing it promotes the synthetic
   * default renderer to a real row (`ensureRendererMaterialized`), so the
   * option targets `renderer-attr` on this renderer — the materialized row
   * persists the color through serialize → hydrate. The option write (`RowEnvelopeDrive.writeTarget`)
   * materializes before the write lands; `checkColor` above dual-reads so an
   * un-materialized renderer still paints the source's value.
   */
  override optionContributions(): readonly ModelRowOptions.Contribution[] {
    const source = this.parent;
    if (!(source instanceof SourceRow)) return [];
    return [
      {
        key: "checkmark:color",
        label: "Color",
        tier: "section",
        priority: 50,
        section: "Appearance",
        target: { kind: "renderer-attr", rowId: this.id, attr: "checkColor" },
        control: { kind: "color" },
      },
    ];
  }
}

/** Types that go with `ColumnCheckmarkRow`: `Writer`. */
export namespace ColumnCheckmarkRow {
  /** A `column.checkmark` renderer's writers, reached through `columnCheckmarkWriterOf(row)` only. */
  export interface Writer {
    /** The row these writers write. */
    readonly row: ColumnCheckmarkRow;
    /** Checked write of `checked` for `date`. */
    write(date: NaiveDate.Partial, checked: boolean, actor: Actor): Promise<void>;
    /** Checked write of the negation of the loaded value (unset reads `false`). */
    toggle(date: NaiveDate.Partial, actor: Actor): Promise<void>;
  }
}
